import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { format } from "date-fns";
import { CalendarX, Trash2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loading } from "@/components/ui/loading";
import { useToast } from "@/components/ui/use-toast";
import { axiosInstance } from "@/pages/auth/auth.context";
import { parseLocalDate } from "@/lib/utils";
import { FullDateAdminDto } from "@/types/fullDate";

/**
 * Marks dates as fully booked. The public bid request form shows the upcoming
 * ones as a heads up, and nothing else in the app changes: a bid for a full date
 * still arrives as a normal new bid.
 */
export default function ManageFullDates() {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: fullDates, isLoading } = useQuery({
    queryKey: ["full-dates-admin"],
    queryFn: async (): Promise<FullDateAdminDto[]> => {
      const response = await axiosInstance.get("full-dates/all");
      return response.data;
    },
  });

  const resetForm = () => {
    setDate("");
    setNote("");
  };

  const { mutate: markFull, isPending } = useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.post("full-dates", {
        date,
        note: note.trim() === "" ? null : note.trim(),
      });
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Date Marked Full",
        description: "The bid request form will show it from now on.",
      });
      queryClient.invalidateQueries({ queryKey: ["full-dates-admin"] });
      queryClient.invalidateQueries({ queryKey: ["full-dates"] });
      resetForm();
    },
    onError: (error) => {
      const alreadyMarked =
        axios.isAxiosError(error) && error.response?.status === 409;
      toast({
        title: "Error",
        description: alreadyMarked
          ? "That date is already marked full."
          : "Failed to mark the date full.",
        variant: "destructive",
      });
    },
  });

  const { mutate: unmarkFull } = useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(`full-dates/${id}`);
    },
    onSuccess: () => {
      toast({
        title: "Date Unmarked",
        description: "It no longer shows on the bid request form.",
      });
      queryClient.invalidateQueries({ queryKey: ["full-dates-admin"] });
      queryClient.invalidateQueries({ queryKey: ["full-dates"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to unmark the date.",
        variant: "destructive",
      });
    },
  });

  const today = format(new Date(), "yyyy-MM-dd");

  return (
    <div className="container mx-auto space-y-6 py-10 px-4">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Full Dates</h1>
        <p className="mt-2 text-muted-foreground">
          Dates marked here appear on the public bid request form as a heads up.
          It is advisory only — customers can still submit a request for a full
          date, and it arrives as a normal new bid.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Mark a date full</CardTitle>
          <CardDescription>
            The note is optional and is shown to the customer alongside the
            date.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-4 sm:flex-row sm:items-end"
            onSubmit={(event) => {
              event.preventDefault();
              if (date) {
                markFull();
              }
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="full-date">Date</Label>
              <Input
                id="full-date"
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                required
              />
            </div>
            <div className="flex-1 space-y-2">
              <Label htmlFor="full-date-note">Note (optional)</Label>
              <Input
                id="full-date-note"
                type="text"
                maxLength={500}
                placeholder="One table still open"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
            <Button type="submit" disabled={!date || isPending}>
              {isPending ? "Marking..." : "Mark Full"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Marked dates</CardTitle>
          <CardDescription>
            Past dates stop showing on the form by themselves, so there is
            nothing to clean up.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Loading />
          ) : !fullDates || fullDates.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No dates are marked full, so the bid request form shows no notice.
            </p>
          ) : (
            <ul className="divide-y">
              {fullDates.map((fullDate) => {
                const parsed = parseLocalDate(fullDate.date);
                const isPast = fullDate.date < today;
                return (
                  <li
                    key={fullDate.id}
                    className="flex items-center justify-between gap-4 py-3"
                  >
                    <div className={isPast ? "opacity-50" : undefined}>
                      <div className="flex items-center gap-2 font-medium">
                        <CalendarX
                          className="h-4 w-4 text-muted-foreground"
                          aria-hidden="true"
                        />
                        {parsed
                          ? format(parsed, "EEEE, MMMM d, yyyy")
                          : fullDate.date}
                        {isPast ? (
                          <span className="text-xs text-muted-foreground">
                            (past — no longer shown)
                          </span>
                        ) : null}
                      </div>
                      {fullDate.note ? (
                        <p className="mt-1 text-sm text-muted-foreground">
                          {fullDate.note}
                        </p>
                      ) : null}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Unmark ${fullDate.date}`}
                      onClick={() => unmarkFull(fullDate.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
